import React, { useEffect, useRef, useState } from 'react';
import { Hand, Waves, Mic2, Sparkles, Brain } from 'lucide-react';

const PX = 600;

export const TractStage = ({ lab, compact, onReady }) => {
  const { eng, mode, setMode, status, modelReady } = lab;
  const hostRef = useRef(null);
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [ready, setReady] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [wobble, setWobble] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, e.contentRect.width / PX)));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let dead = false;
    const host = hostRef.current;
    import('../../lib/samuel/engine').then(({ loadPinkTrombone }) => loadPinkTrombone()).then(() => {
      if (dead || host.firstChild) return;
      const el = document.createElement('pink-trombone');
      host.appendChild(el);
      eng.init(el).then(() => { if (dead) eng.destroy(); else { setReady(true); onReady?.(); } });
    });
    return () => { dead = true; eng.destroy(); };
  }, [eng]);

  useEffect(() => {
    const el = hostRef.current?.firstChild;
    if (!el || !ready) return;
    el.interactive = manual;
    const onBox = (ev) => {
      ev.preventDefault();
      const { frequency, tenseness } = ev.detail || {};
      if (frequency !== undefined && tenseness !== undefined) eng.voice(frequency, tenseness);
    };
    el.addEventListener('voicebox', onBox);
    return () => el.removeEventListener('voicebox', onBox);
  }, [manual, ready, eng]);

  const toggleManual = () => {
    if (manual) { setManual(false); eng.endVoice(); return; }
    lab.stopAll(); setManual(true); eng.resume(); eng.startVoice();
  };
  const toggleVoice = () => { eng.setVoiceOn(!voiceOn); setVoiceOn(!voiceOn); };
  const toggleWobble = () => { eng.resume(); eng.setWobble(!wobble); setWobble(!wobble); };

  const Pill = ({ on, onClick, icon: I, children, id }) => (
    <button onClick={onClick} data-testid={id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? 'bg-amber-400 text-slate-950' : 'bg-slate-800 text-cyan-100 hover:bg-slate-700'}`}>
      <I className="w-3.5 h-3.5" /> {children}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="samuel-tract-stage">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-full bg-slate-900 border border-cyan-500/25 p-1" data-testid="samuel-mode-switch">
          <button onClick={() => setMode('samuel')} data-testid="mode-samuel" className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-colors ${mode === 'samuel' ? 'bg-cyan-400 text-slate-950' : 'text-cyan-200'}`}><Brain className="w-3.5 h-3.5" /> Samuel · neurale</button>
          <button onClick={() => setMode('vf')} data-testid="mode-vf" className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-colors ${mode === 'vf' ? 'bg-amber-400 text-slate-950' : 'text-cyan-200'}`}><Sparkles className="w-3.5 h-3.5" /> VocalFitness · fonetico</button>
        </div>
        <span className={`ml-auto text-[11px] px-2 py-1 rounded-full ${ready && modelReady ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-400'}`} data-testid="samuel-ready">
          {ready && modelReady ? 'Modello pronto' : 'Caricamento modello…'}
        </span>
      </div>
      <div ref={wrapRef} className="w-full overflow-hidden rounded-2xl bg-white relative" style={{ height: PX * scale, maxWidth: compact ? 520 : 600 }}>
        <div ref={hostRef} className="relative origin-top-left" style={{ width: PX, height: PX, transform: `scale(${scale})` }} data-testid="samuel-tract" />
        {status && (
          <div className="absolute inset-x-0 top-1/3 flex justify-center pointer-events-none">
            <span className="px-4 py-2 rounded-full bg-slate-950/85 text-amber-300 text-sm font-semibold" data-testid="samuel-status">{status}</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Pill on={manual} onClick={toggleManual} icon={Hand} id="toggle-manual">{manual ? 'Controllo manuale attivo' : 'Controllo manuale'}</Pill>
        <Pill on={!voiceOn} onClick={toggleVoice} icon={Mic2} id="toggle-voice">{voiceOn ? 'Disattiva voce' : 'Voce disattivata'}</Pill>
        <Pill on={wobble} onClick={toggleWobble} icon={Waves} id="toggle-wobble">{wobble ? 'Vibrato attivo' : 'Vibrato'}</Pill>
      </div>
      {manual && <p className="text-[11px] text-cyan-200/70">Trascina la lingua e le costrizioni sul tratto e la barra «voicebox» per intonare: così vedi quanto è difficile.</p>}
    </div>
  );
};
